<template>
  <AdminLayout page-title="Preferences" page-subtitle="Customize your admin dashboard experience and workflow settings">
    <div class="space-y-8">
      <!-- Display Preferences -->
      <div class="admin-stat-card">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-xl font-semibold text-white">Display & Interface</h3>
            <p class="text-slate-400 text-sm">Customize the appearance and behavior of your admin dashboard</p>
          </div>
        </div>

        <div class="space-y-6">
          <!-- Theme Settings -->
          <div class="bg-slate-700/30 rounded-xl p-6">
            <h4 class="font-semibold text-white mb-4">Theme & Appearance</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="form-label">Color Theme</label>
                <div class="grid grid-cols-2 gap-3">
                  <label class="flex items-center space-x-3 p-3 bg-slate-600/30 rounded-xl cursor-pointer hover:bg-slate-600/50 transition-colors">
                    <input
                      v-model="preferences.theme"
                      type="radio"
                      value="dark"
                      class="w-4 h-4 text-emerald-500 bg-slate-700 border-slate-600 focus:ring-emerald-500"
                    >
                    <div class="flex items-center space-x-2">
                      <div class="w-4 h-4 bg-slate-800 rounded-full border border-slate-600"></div>
                      <span class="text-slate-300">Dark</span>
                    </div>
                  </label>
                  <label class="flex items-center space-x-3 p-3 bg-slate-600/30 rounded-xl cursor-pointer hover:bg-slate-600/50 transition-colors">
                    <input
                      v-model="preferences.theme"
                      type="radio"
                      value="auto"
                      class="w-4 h-4 text-emerald-500 bg-slate-700 border-slate-600 focus:ring-emerald-500"
                    >
                    <div class="flex items-center space-x-2">
                      <div class="w-4 h-4 bg-gradient-to-r from-slate-300 to-slate-800 rounded-full"></div>
                      <span class="text-slate-300">Auto</span>
                    </div>
                  </label>
                </div>
              </div>
              
              <div>
                <label class="form-label">Sidebar Style</label>
                <select v-model="preferences.sidebarStyle" class="form-select">
                  <option value="expanded">Always Expanded</option>
                  <option value="collapsed">Auto Collapse</option>
                  <option value="overlay">Overlay Mode</option>
                </select>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div>
                <label class="form-label">Density</label>
                <select v-model="preferences.density" class="form-select">
                  <option value="compact">Compact</option>
                  <option value="comfortable">Comfortable</option>
                  <option value="spacious">Spacious</option>
                </select>
              </div>
              
              <div>
                <label class="form-label">Animation Level</label>
                <select v-model="preferences.animations" class="form-select">
                  <option value="none">None</option>
                  <option value="reduced">Reduced</option>
                  <option value="full">Full</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Dashboard Layout -->
          <div class="bg-slate-700/30 rounded-xl p-6">
            <h4 class="font-semibold text-white mb-4">Dashboard Layout</h4>
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium text-white">Show Quick Stats</p>
                  <p class="text-sm text-slate-400">Display key metrics at the top of the dashboard</p>
                </div>
                <label class="toggle-switch">
                  <input v-model="preferences.showQuickStats" type="checkbox" class="sr-only">
                  <div class="toggle-slider"></div>
                </label>
              </div>
              
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium text-white">Show Recent Activity</p>
                  <p class="text-sm text-slate-400">Display recent system activities on the dashboard</p>
                </div>
                <label class="toggle-switch">
                  <input v-model="preferences.showRecentActivity" type="checkbox" class="sr-only">
                  <div class="toggle-slider"></div>
                </label>
              </div>
              
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium text-white">Auto-refresh Data</p>
                  <p class="text-sm text-slate-400">Automatically refresh dashboard data every 30 seconds</p>
                </div>
                <label class="toggle-switch">
                  <input v-model="preferences.autoRefresh" type="checkbox" class="sr-only">
                  <div class="toggle-slider"></div>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Workflow Preferences -->
      <div class="admin-stat-card">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-xl font-semibold text-white">Workflow & Productivity</h3>
            <p class="text-slate-400 text-sm">Configure settings to optimize your workflow and productivity</p>
          </div>
        </div>

        <div class="space-y-6">
          <!-- Table Settings -->
          <div class="bg-slate-700/30 rounded-xl p-6">
            <h4 class="font-semibold text-white mb-4">Table & List Settings</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="form-label">Default Page Size</label>
                <select v-model="preferences.defaultPageSize" class="form-select">
                  <option value="10">10 items</option>
                  <option value="25">25 items</option>
                  <option value="50">50 items</option>
                  <option value="100">100 items</option>
                </select>
              </div>
              
              <div>
                <label class="form-label">Date Format</label>
                <select v-model="preferences.dateFormat" class="form-select">
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  <option value="relative">Relative (2 days ago)</option>
                </select>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div>
                <label class="form-label">Time Format</label>
                <select v-model="preferences.timeFormat" class="form-select">
                  <option value="12">12-hour (AM/PM)</option>
                  <option value="24">24-hour</option>
                </select>
              </div>
              
              <div>
                <label class="form-label">Currency Display</label>
                <select v-model="preferences.currencyFormat" class="form-select">
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="CAD">CAD (C$)</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Quick Actions -->
          <div class="bg-slate-700/30 rounded-xl p-6">
            <h4 class="font-semibold text-white mb-4">Quick Actions</h4>
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium text-white">Enable Keyboard Shortcuts</p>
                  <p class="text-sm text-slate-400">Use keyboard shortcuts for common actions</p>
                </div>
                <label class="toggle-switch">
                  <input v-model="preferences.keyboardShortcuts" type="checkbox" class="sr-only">
                  <div class="toggle-slider"></div>
                </label>
              </div>
              
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium text-white">Confirm Destructive Actions</p>
                  <p class="text-sm text-slate-400">Show confirmation dialogs for delete operations</p>
                </div>
                <label class="toggle-switch">
                  <input v-model="preferences.confirmDestructive" type="checkbox" class="sr-only">
                  <div class="toggle-slider"></div>
                </label>
              </div>
              
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium text-white">Auto-save Forms</p>
                  <p class="text-sm text-slate-400">Automatically save form data as you type</p>
                </div>
                <label class="toggle-switch">
                  <input v-model="preferences.autoSaveForms" type="checkbox" class="sr-only">
                  <div class="toggle-slider"></div>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Data & Export Preferences -->
      <div class="admin-stat-card">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-xl font-semibold text-white">Data & Export Settings</h3>
            <p class="text-slate-400 text-sm">Configure how data is displayed and exported from the system</p>
          </div>
        </div>

        <div class="space-y-6">
          <!-- Export Settings -->
          <div class="bg-slate-700/30 rounded-xl p-6">
            <h4 class="font-semibold text-white mb-4">Export Preferences</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="form-label">Default Export Format</label>
                <select v-model="preferences.defaultExportFormat" class="form-select">
                  <option value="csv">CSV</option>
                  <option value="xlsx">Excel (XLSX)</option>
                  <option value="pdf">PDF</option>
                  <option value="json">JSON</option>
                </select>
              </div>
              
              <div>
                <label class="form-label">Export Filename Pattern</label>
                <select v-model="preferences.exportFilenamePattern" class="form-select">
                  <option value="default">Default (export_YYYY-MM-DD)</option>
                  <option value="descriptive">Descriptive (users_export_2024-01-15)</option>
                  <option value="timestamp">Timestamp (export_20240115_143022)</option>
                </select>
              </div>
            </div>

            <div class="space-y-4 mt-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium text-white">Include Headers in Exports</p>
                  <p class="text-sm text-slate-400">Add column headers to exported data</p>
                </div>
                <label class="toggle-switch">
                  <input v-model="preferences.includeHeaders" type="checkbox" class="sr-only">
                  <div class="toggle-slider"></div>
                </label>
              </div>
              
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium text-white">Include Metadata</p>
                  <p class="text-sm text-slate-400">Include export date, user, and filter information</p>
                </div>
                <label class="toggle-switch">
                  <input v-model="preferences.includeMetadata" type="checkbox" class="sr-only">
                  <div class="toggle-slider"></div>
                </label>
              </div>
            </div>
          </div>

          <!-- Data Filtering -->
          <div class="bg-slate-700/30 rounded-xl p-6">
            <h4 class="font-semibold text-white mb-4">Data Filtering</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="form-label">Default Date Range</label>
                <select v-model="preferences.defaultDateRange" class="form-select">
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="quarter">This Quarter</option>
                  <option value="year">This Year</option>
                  <option value="all">All Time</option>
                </select>
              </div>
              
              <div>
                <label class="form-label">Remember Filter Settings</label>
                <select v-model="preferences.rememberFilters" class="form-select">
                  <option value="session">For This Session</option>
                  <option value="always">Always Remember</option>
                  <option value="never">Never Remember</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- System Preferences -->
      <div class="admin-stat-card">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-xl font-semibold text-white">System & Performance</h3>
            <p class="text-slate-400 text-sm">Configure system behavior and performance settings</p>
          </div>
        </div>

        <div class="space-y-6">
          <!-- Performance Settings -->
          <div class="bg-slate-700/30 rounded-xl p-6">
            <h4 class="font-semibold text-white mb-4">Performance Settings</h4>
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium text-white">Enable Data Caching</p>
                  <p class="text-sm text-slate-400">Cache frequently accessed data for better performance</p>
                </div>
                <label class="toggle-switch">
                  <input v-model="preferences.enableCaching" type="checkbox" class="sr-only">
                  <div class="toggle-slider"></div>
                </label>
              </div>
              
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium text-white">Lazy Load Images</p>
                  <p class="text-sm text-slate-400">Load images only when they come into view</p>
                </div>
                <label class="toggle-switch">
                  <input v-model="preferences.lazyLoadImages" type="checkbox" class="sr-only">
                  <div class="toggle-slider"></div>
                </label>
              </div>
              
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium text-white">Preload Critical Data</p>
                  <p class="text-sm text-slate-400">Preload important data in the background</p>
                </div>
                <label class="toggle-switch">
                  <input v-model="preferences.preloadData" type="checkbox" class="sr-only">
                  <div class="toggle-slider"></div>
                </label>
              </div>
            </div>
          </div>

          <!-- Session Settings -->
          <div class="bg-slate-700/30 rounded-xl p-6">
            <h4 class="font-semibold text-white mb-4">Session Management</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="form-label">Session Timeout</label>
                <select v-model="preferences.sessionTimeout" class="form-select">
                  <option value="30">30 minutes</option>
                  <option value="60">1 hour</option>
                  <option value="120">2 hours</option>
                  <option value="240">4 hours</option>
                  <option value="480">8 hours</option>
                  <option value="never">Never</option>
                </select>
              </div>
              
              <div>
                <label class="form-label">Auto-logout Warning</label>
                <select v-model="preferences.logoutWarning" class="form-select">
                  <option value="5">5 minutes before</option>
                  <option value="10">10 minutes before</option>
                  <option value="15">15 minutes before</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Save Settings -->
      <div class="flex justify-end space-x-4">
        <button @click="resetToDefaults" class="btn-secondary">
          Reset to Defaults
        </button>
        <button @click="savePreferences" class="btn-oilbuddy-primary">
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
          Save Preferences
        </button>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import AdminLayout from './AdminLayout.vue'

interface Preferences {
  // Display preferences
  theme: 'dark' | 'auto'
  sidebarStyle: 'expanded' | 'collapsed' | 'overlay'
  density: 'compact' | 'comfortable' | 'spacious'
  animations: 'none' | 'reduced' | 'full'
  showQuickStats: boolean
  showRecentActivity: boolean
  autoRefresh: boolean
  
  // Workflow preferences
  defaultPageSize: string
  dateFormat: string
  timeFormat: string
  currencyFormat: string
  keyboardShortcuts: boolean
  confirmDestructive: boolean
  autoSaveForms: boolean
  
  // Export preferences
  defaultExportFormat: string
  exportFilenamePattern: string
  includeHeaders: boolean
  includeMetadata: boolean
  defaultDateRange: string
  rememberFilters: string
  
  // System preferences
  enableCaching: boolean
  lazyLoadImages: boolean
  preloadData: boolean
  sessionTimeout: string
  logoutWarning: string
}

// Load preferences from localStorage or use defaults
const loadPreferences = (): Preferences => {
  const saved = localStorage.getItem('adminPreferences')
  if (saved) {
    try {
      return { ...getDefaultPreferences(), ...JSON.parse(saved) }
    } catch {
      return getDefaultPreferences()
    }
  }
  return getDefaultPreferences()
}

const getDefaultPreferences = (): Preferences => ({
  // Display preferences
  theme: 'dark',
  sidebarStyle: 'expanded',
  density: 'comfortable',
  animations: 'full',
  showQuickStats: true,
  showRecentActivity: true,
  autoRefresh: true,
  
  // Workflow preferences
  defaultPageSize: '25',
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12',
  currencyFormat: 'USD',
  keyboardShortcuts: true,
  confirmDestructive: true,
  autoSaveForms: true,
  
  // Export preferences
  defaultExportFormat: 'csv',
  exportFilenamePattern: 'descriptive',
  includeHeaders: true,
  includeMetadata: true,
  defaultDateRange: 'month',
  rememberFilters: 'session',
  
  // System preferences
  enableCaching: true,
  lazyLoadImages: true,
  preloadData: true,
  sessionTimeout: '120',
  logoutWarning: '10'
})

// Reactive state
const preferences = ref<Preferences>(loadPreferences())

// Methods
const savePreferences = () => {
  try {
    localStorage.setItem('adminPreferences', JSON.stringify(preferences.value))
    alert('Preferences saved successfully!')
  } catch (error) {
    alert('Failed to save preferences. Please try again.')
  }
}

const resetToDefaults = () => {
  if (confirm('Are you sure you want to reset all preferences to their default values?')) {
    preferences.value = getDefaultPreferences()
    savePreferences()
  }
}
</script>

<style scoped>
.admin-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-emerald-500/30 transition-all duration-300;
}

/* Form Styles */
.form-label {
  @apply block text-sm font-medium text-slate-300 mb-3;
}

.form-select {
  @apply w-full bg-slate-700/50 border border-slate-600/50 text-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all duration-200;
}

/* Toggle Switch Styles */
.toggle-switch {
  @apply relative inline-flex items-center cursor-pointer;
}

.toggle-slider {
  @apply w-11 h-6 bg-slate-600 rounded-full relative transition-colors duration-200;
}

.toggle-slider::after {
  @apply content-[''] absolute top-[2px] left-[2px] bg-white rounded-full h-5 w-5 transition-all duration-200;
}

.toggle-switch input:checked + .toggle-slider {
  @apply bg-gradient-to-r from-emerald-400 to-cyan-400;
}

.toggle-switch input:checked + .toggle-slider::after {
  @apply translate-x-full border-white;
}

/* OilBuddy Button Styles */
.btn-oilbuddy-primary {
  @apply inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-400 to-cyan-400 text-slate-900 font-semibold rounded-lg hover:from-emerald-500 hover:to-cyan-500 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-emerald-500/25;
}

.btn-secondary {
  @apply inline-flex items-center px-6 py-3 bg-slate-700/50 text-slate-300 font-medium rounded-lg hover:bg-slate-600/50 transition-colors duration-200;
}
</style>
